import { Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    AppUpdateService,
    AppVersionAndSessionService,
    AuthenticationService,
    AuthenticationTimeoutService,
    BaseInitializationService,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigRequestsInstanceToken,
    ConfigRequestsInterface,
    ConfigService,
    ConnectivityService,
    CseStockService,
    DateManipulationCheckService,
    EncryptionInitializer,
    EventService,
    INIT_MESSAGE_KEY,
    INIT_MESSAGE_STATE,
    InitError,
    InitMessage,
    InputFieldPropertiesService,
    LocalStorageAppStateService,
    LocalStorageCommonService,
    LocalStorageRecipientsService,
    MaterialService,
    MeasurementToolsService,
    NotificationService,
    NotificationStatusService,
    NotificationUpdateService,
    PwaPushNotificationService,
    SessionService,
    SideMenuInstanceToken,
    SideMenuInterface,
    SynchronizationService,
    TimesheetAndExpensesService,
    UriUtilitiesService,
    UserGidService,
    UserMasterService,
    ValueHelperService,
    WeeklyWorkTimeService,
    WorkScheduleService,
} from '@evorait/pace-core';
import { FormsService } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { forkJoin, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class InitializationService extends BaseInitializationService {
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        @Inject(SideMenuInstanceToken) protected readonly sideMenuService: SideMenuInterface,
        @Inject(ConfigRequestsInstanceToken)
        protected readonly configInterfaceService: ConfigRequestsInterface,
        protected readonly modalService: BsModalService,
        protected readonly localStorageAppStateService: LocalStorageAppStateService,
        protected readonly connectivityService: ConnectivityService,
        protected readonly uriUtilities: UriUtilitiesService,
        protected readonly authenticationService: AuthenticationService,
        protected readonly userMasterService: UserMasterService,
        protected readonly appNotificationService: AppNotificationService,
        protected readonly translate: TranslateService,
        protected readonly eventService: EventService,
        protected readonly synchronizationService: SynchronizationService,
        protected readonly offlineService: OfflineService,
        protected readonly userGidService: UserGidService,
        protected readonly weeklyWorkTimeService: WeeklyWorkTimeService,
        protected readonly configService: ConfigService,
        protected readonly authenticationTimeoutService: AuthenticationTimeoutService,
        protected readonly appVersionService: AppVersionAndSessionService,
        protected readonly localStorageCommonService: LocalStorageCommonService,
        private readonly valueHelperService: ValueHelperService,
        private readonly inputFieldPropertiesService: InputFieldPropertiesService,
        private readonly workScheduleService: WorkScheduleService,
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
        private readonly notificationService: NotificationService,
        private readonly notificationUpdateService: NotificationUpdateService,
        private readonly materialService: MaterialService,
        private readonly cseStockService: CseStockService,
        private readonly encryptionInitializer: EncryptionInitializer,
        private readonly notificationStatusService: NotificationStatusService,
        private readonly measurementToolsService: MeasurementToolsService,
        private readonly localStorageRecipientsService: LocalStorageRecipientsService,
        private readonly appUpdateService: AppUpdateService,
        public readonly modalInstance: BsModalRef,
        protected readonly pwaPushNotificationService: PwaPushNotificationService,
        protected readonly dateManipulationCheckService: DateManipulationCheckService,
        protected readonly sessionService: SessionService,
        private readonly formsService: FormsService,
    ) {
        super(
            log,
            clientManager,
            sideMenuService,
            configInterfaceService,
            modalService,
            localStorageAppStateService,
            connectivityService,
            uriUtilities,
            authenticationService,
            userMasterService,
            appNotificationService,
            translate,
            eventService,
            synchronizationService,
            configService,
            weeklyWorkTimeService,
            offlineService,
            userGidService,
            authenticationTimeoutService,
            appVersionService,
            localStorageCommonService,
            sessionService,
        );
    }

    protected setupInitProcessPipe(): void {
        this.init$ = of(true);
        this.init$ = this.attachPWAAppUpdate(this.init$);
        this.init$ = this.attachEncryptionServiceInit(this.init$).pipe(switchMap(() => this.attachRehydration()));
        this.init$ = this.attachInternetConnectionCheck(this.init$);
        this.init$ = this.attachDateManipulationCheckServiceInit(this.init$);
        this.init$ = this.attachUserMasterServiceInit(this.init$);
        this.init$ = this.attachPWAPushNotifications(this.init$);
        this.init$ = this.attachConfigInterfaceInit(this.init$);
        // this.init$ = this.attachVersionAndSessionSyncWithGateway(this.init$);
        // this.init$ = this.attachSessionCheck(this.init$);
        this.init$ = this.attachInitDurationLogger(this.init$);
        this.init$ = this.attachAppInitStateChange(this.init$);
    }

    public init(): Observable<void> {
        return super.init().pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    this.log.warn(
                        'received an initialization error, but it can be handled and we will just continue',
                        error,
                    );
                    return of(undefined);
                }
                this.log.warn('received an initialization error, that can not be handled', error);
                return throwError(() => error);
            }),
            tap(() => {
                this.pwaPushNotificationService.askUserForPushNotificationPermission();
            }),
        );
    }

    /**
     * Make sure the odata consuming services are initialized in the correct order
     *
     * @param obs$ the observable to attach the initialization to
     * @returns the observable with the initialization attached
     */
    public attachServiceInit(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.SERVICE_INIT,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream(`APP_START_INIT_OTHER_SERVICES`),
                of(`0s`),
            ),
        );
        let previousError: InitError;
        let startTimer: moment.Moment;
        return obs$
            .pipe(
                catchError((error) => {
                    if (this.errorCanBeHandled(error)) {
                        previousError = <InitError>error;
                        return of(undefined);
                    }

                    return throwError(() => error);
                }),
                mergeMap(() => {
                    startTimer = moment();

                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream('APP_START_INIT_CONFIG_VALUEHELPER_INPUTFIELDPROPERTIES'),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return forkJoin([
                        this.configService.init(),
                        this.valueHelperService.init(),
                        this.inputFieldPropertiesService.init(),
                    ]);
                }),
                catchError((error) => {
                    if (error instanceof InitError) {
                        return throwError(() => error);
                    }
                    this.log.warn(
                        `InitializationService: attachServiceInit: Error while initializing services: ${JSON.stringify(
                            error,
                        )}`,
                    );
                    return of(undefined);
                }),
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream('APP_START_INIT_CONFIG_VALUEHELPER_INPUTFIELDPROPERTIES'),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );

                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(`APP_START_INIT_WORKSCHEDULESERVICE`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return this.workScheduleService.init();
                }),
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_WORKSCHEDULESERVICE`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(`APP_START_INIT_TIMESHEET_AND_EXPENSES`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return this.timesheetAndExpensesService.init();
                }),
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_TIMESHEET_AND_EXPENSES`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(`APP_START_INIT_WEEKLY_WORK_TIME`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return this.weeklyWorkTimeService.init();
                }),
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_WEEKLY_WORK_TIME`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(`APP_START_INIT_NOTIFICATION_STATUS`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return this.notificationStatusService.init();
                }),
            )
            .pipe(
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_NOTIFICATION_STATUS`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(`APP_START_INIT_NOTIFICATION`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return this.notificationService.init(true);
                }),
                catchError((error) => {
                    if (error instanceof InitError) {
                        return throwError(() => error);
                    }
                    this.log.warn(
                        `InitializationService: attachServiceInit: Error while initializing services: `,
                        error,
                    );
                    return of(undefined);
                }),
                mergeMap(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_NOTIFICATION`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.PENDING,
                            this.translate.stream(
                                `APP_START_INIT_NOTIFICATIONUPDATE_MATERIAL_CSESTOCK_MEASUREMENTTOOLS`,
                            ),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    return forkJoin([
                        this.notificationUpdateService.init(false, true),
                        this.materialService.init(),
                        this.cseStockService.init(),
                        this.measurementToolsService.init(),
                        this.localStorageRecipientsService.init(),
                    ]);
                }),
                catchError((error) => {
                    if (error instanceof InitError) {
                        return throwError(() => error);
                    }
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.ERROR,
                            this.translate.stream(
                                `APP_START_INIT_NOTIFICATIONUPDATE_MATERIAL_CSESTOCK_MEASUREMENTTOOLS_ERROR`,
                            ),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.log.error(
                        `InitializationService: attachServiceInit: Error while initializing services: ${JSON.stringify(
                            error,
                        )}`,
                    );
                    return throwError(() => new InitError(<Error>error, true));
                }),
                map(() => {
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(
                                `APP_START_INIT_NOTIFICATIONUPDATE_MATERIAL_CSESTOCK_MEASUREMENTTOOLS`,
                            ),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.initMessagesSubject.next(
                        new InitMessage(
                            INIT_MESSAGE_KEY.SERVICE_INIT,
                            INIT_MESSAGE_STATE.SUCCESS,
                            this.translate.stream(`APP_START_INIT_OTHER_SERVICES`),
                            of(`${moment().diff(startTimer, 'seconds')}s`),
                        ),
                    );
                    this.log.debug(
                        `InitializationService: attachServiceInit: time taken: ${
                            moment().valueOf() - startTimer.valueOf()
                        } ms`,
                    );
                    return undefined;
                }),
                mergeMap((result) => {
                    if (typeof previousError !== 'undefined') {
                        return throwError(() => previousError);
                    }
                    return of(result);
                }),
            );
    }

    // Function to load any effects that should happen after initialization is completed.
    public executeRemainingSideEffects(): void {
        this.formsService.restoreLastOpenedForm();
    }
    /**
     * Initialize Encryption service
     *
     * @param obs$ the observable to attach the initialization to
     * @returns the observable with the initialization attached
     */
    private attachEncryptionServiceInit(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_ENCRYPTION'),
                of('0s'),
            ),
        );
        let startTimer: moment.Moment;
        return obs$.pipe(
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_ENCRYPTION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.encryptionInitializer.init()),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_ENCRYPTION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return of(undefined);
            }),
            tap(() => {
                this.log.debug(
                    `InitializationService: attachEncryptionServiceInit: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                );
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_ENCRYPTION_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
        );
    }

    private attachDateManipulationCheckServiceInit(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.DATE_MANIPULATION_SERVICE,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_DATE_MANIPULATION'),
                of(`0s`),
            ),
        );
        let startTimer: moment.Moment;
        let previousError: InitError;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = error;
                    return of(undefined);
                }
                startTimer = moment();
                return throwError(() => error);
            }),
            tap(() => {
                startTimer = moment();
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.DATE_MANIPULATION_SERVICE,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_DATE_MANIPULATION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
            }),
            mergeMap(() => this.dateManipulationCheckService.init()),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.DATE_MANIPULATION_SERVICE,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_DATE_MANIPULATION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                if (previousError) {
                    return throwError(() => previousError);
                }
                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.DATE_MANIPULATION_SERVICE,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_DATE_MANIPULATION_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
            finalize(() => {
                this.log.debug(
                    `InitializationService: attachDateManipulationCheckServiceInit: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                );
            }),
        );
    }

    /**
     * Register for push notifications at the gateway
     *
     * @param obs$ the observable to attach the initialization to
     * @returns the observable with the initialization attached
     */
    // private attachRegisterPushNotifications(obs$: Observable<unknown>): Observable<void> {
    //     let startTimer: moment.Moment;
    //     return obs$.pipe(
    //         tap(
    //             () => (startTimer = moment()),
    //             () => (startTimer = moment()),
    //         ),
    //         tap(() =>
    //             this.initMessagesSubject.next(
    //                 new InitMessage(
    //                     INIT_MESSAGE_KEY.PUSH_NOTIFICATIONS,
    //                     INIT_MESSAGE_STATE.PENDING,
    //                     this.translate.stream('APP_START_INFO_PUSH'),
    //                     of(`${moment().diff(startTimer, 'seconds')}s`),
    //                 ),
    //             ),
    //         ),
    //         mergeMap(() => this.pushNotificationService.registerForPushNotifications()),
    //         map((deviceToken) => {
    //             this.log.debug(
    //                 `InitializationService: attachRegisterPushNotifications: Device token is '${deviceToken}'`,
    //             );
    //
    //             this.initMessagesSubject.next(
    //                 new InitMessage(
    //                     INIT_MESSAGE_KEY.PUSH_NOTIFICATIONS,
    //                     deviceToken ? INIT_MESSAGE_STATE.SUCCESS : INIT_MESSAGE_STATE.WARNING,
    //                     this.translate.stream('APP_START_INFO_PUSH'),
    //                     of(`${moment().diff(startTimer, 'seconds')}s`),
    //                 ),
    //             );
    //         }),
    //         catchError((error) => {
    //             if (error instanceof InitError) {
    //                 return throwError(() => error);
    //             }
    //
    //             this.initMessagesSubject.next(
    //                 new InitMessage(
    //                     INIT_MESSAGE_KEY.PUSH_NOTIFICATIONS,
    //                     INIT_MESSAGE_STATE.ERROR,
    //                     this.translate.stream('APP_START_ERROR_PUSH_REGISTRATION_FAILED'),
    //                     of(`${moment().diff(startTimer, 'seconds')}s`),
    //                 ),
    //             );
    //             return throwError(() => new InitError(<Error>error));
    //         }),
    //         finalize(() =>
    //             this.log.debug(
    //                 `InitializationService: attachRegisterPushNotifications: time taken: ${
    //                     moment().valueOf() - startTimer.valueOf()
    //                 } ms`,
    //             ),
    //         ),
    //     );
    // }

    private attachPWAAppUpdate(obs$: Observable<unknown>): Observable<boolean> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.PWA_APP_UPDATE,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_PWA_APP_UPDATE'),
                of(`0s`),
            ),
        );
        let previousError: InitError;
        let startTimer: moment.Moment;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = <InitError>error;
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_APP_UPDATE,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_PWA_APP_UPDATE'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.appUpdateService.init()),
            map(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_APP_UPDATE,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_PWA_APP_UPDATE'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return true;
            }),
            mergeMap((result) => {
                if (typeof previousError !== 'undefined') {
                    return throwError(() => previousError);
                }
                return of(result);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_APP_UPDATE,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_PWA_APP_UPDATE'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
            finalize(() =>
                this.log.debug(
                    `InitializationService: attachAppUpdate: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                ),
            ),
        );
    }

    public attachPWAPushNotifications(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.PWA_PUSH_NOTIFICATIONS,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_PWA_PUSH_NOTIFICATIONS'),
                of(`0s`),
            ),
        );
        let previousError: InitError;
        let startTimer: moment.Moment;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    previousError = <InitError>error;
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_PUSH_NOTIFICATIONS,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_PWA_PUSH_NOTIFICATIONS'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.pwaPushNotificationService.init()),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_PUSH_NOTIFICATIONS,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_PWA_PUSH_NOTIFICATIONS'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return of(undefined);
            }),
            mergeMap((result) => {
                if (typeof previousError !== 'undefined') {
                    return throwError(() => previousError);
                }
                return of(result);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.PWA_PUSH_NOTIFICATIONS,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_PWA_PUSH_NOTIFICATIONS_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
            finalize(() =>
                this.log.debug(
                    `InitializationService: attachPwaPushNotificationsService: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                ),
            ),
        );
    }
}
